import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'

import type {
  ToolbarActivityListItem,
  ToolbarFeedResponseItemsItem,
} from '#/shared/api/generated/shipment/models'
import {
  getListFeedApiV1ToolbarFeedGetQueryKey,
  listFeedApiV1ToolbarFeedGet,
} from '#/shared/api/generated/shipment/theCargoShipment'

import { toShipmentActivityFeedItem } from './use-shipment-activity-feed'
import type { ShipmentActivityFeedItem } from './use-shipment-activity-feed'
import { toShipmentContractFeedItem } from './use-shipment-contract-feed'
import type { ShipmentContractFeedItem } from './use-shipment-contract-feed'
import { toShipmentEmailFeedItem } from './use-shipment-email-feed'
import type { ShipmentEmailFeedItem } from './use-shipment-email-feed'
import { toShipmentFileFeedItem } from './use-shipment-file-feed'
import type { ShipmentFileFeedItem } from './use-shipment-file-feed'
import { toShipmentNoteFeedItem } from './use-shipment-note-feed'
import type { ShipmentNoteFeedItem } from './use-shipment-note-feed'
import { toShipmentPaymentFeedItem } from './use-shipment-payment-feed'
import type { ShipmentPaymentFeedItem } from './use-shipment-payment-feed'
import { toShipmentSmsFeedItem } from './use-shipment-sms-feed'
import type { ShipmentSmsFeedItem } from './use-shipment-sms-feed'
import { toShipmentTaskFeedItem } from './use-shipment-task-feed'
import type { ShipmentTaskFeedItem } from './use-shipment-task-feed'

const DEFAULT_FEED_SIZE = 20

export type ShipmentFeedItem =
  | { kind: 'note'; data: ShipmentNoteFeedItem }
  | { kind: 'task'; data: ShipmentTaskFeedItem }
  | { kind: 'file'; data: ShipmentFileFeedItem }
  | { kind: 'contract'; data: ShipmentContractFeedItem }
  | { kind: 'sms'; data: ShipmentSmsFeedItem }
  | { kind: 'email'; data: ShipmentEmailFeedItem }
  | { kind: 'payment'; data: ShipmentPaymentFeedItem }
  | { kind: 'activity'; data: ShipmentActivityFeedItem }

interface UseShipmentFeedOptions {
  enabled?: boolean
  size?: number
}

export interface UseShipmentFeedResult {
  items: ShipmentFeedItem[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
}

function toShipmentFeedItem(
  raw: ToolbarFeedResponseItemsItem,
): ShipmentFeedItem {
  switch (raw.kind) {
    case 'note':
      return { kind: 'note', data: toShipmentNoteFeedItem(raw) }
    case 'task':
      return { kind: 'task', data: toShipmentTaskFeedItem(raw) }
    case 'file':
      return { kind: 'file', data: toShipmentFileFeedItem(raw) }
    case 'contract':
      return {
        kind: 'contract',
        data: toShipmentContractFeedItem(raw),
      }
    case 'sms':
      return { kind: 'sms', data: toShipmentSmsFeedItem(raw) }
    case 'email':
      return { kind: 'email', data: toShipmentEmailFeedItem(raw) }
    case 'payment':
      return {
        kind: 'payment',
        data: toShipmentPaymentFeedItem(raw),
      }
    default:
      return {
        kind: 'activity',
        data: toShipmentActivityFeedItem(raw as ToolbarActivityListItem),
      }
  }
}

export function useShipmentFeed(
  shipmentId: string | undefined,
  { enabled = true, size = DEFAULT_FEED_SIZE }: UseShipmentFeedOptions = {},
): UseShipmentFeedResult {
  const armed = enabled && Boolean(shipmentId)
  const baseParams = { shipment_id: shipmentId ?? '', size }

  const query = useInfiniteQuery({
    queryKey: getListFeedApiV1ToolbarFeedGetQueryKey(baseParams),
    queryFn: ({ pageParam, signal }) =>
      listFeedApiV1ToolbarFeedGet({ ...baseParams, cursor: pageParam }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.data.has_more ? lastPage.data.next_cursor : null,
    enabled: armed,
  })

  const items = useMemo<ShipmentFeedItem[]>(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data.items.map(toShipmentFeedItem),
      ),
    [query.data],
  )

  return {
    items,
    isLoading: query.isLoading && armed,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    fetchNextPage: () => {
      void query.fetchNextPage()
    },
  }
}
